import { LevelLite } from '../../curriculum/adapters/types';
import { Squad } from '../../../types';
import { listSquadTeachingDates } from './calendarSync';

export type SessionSlotStatus = 'completed' | 'live' | 'next' | 'future';

export interface ResolvedSessionSlot {
  index: number; // 1-based
  levelId: string;
  levelTitle: string;
  curriculumId?: string;
  sourceLevelId?: string;
  date?: string; // YYYY-MM-DD
  eventId?: string;
  status: SessionSlotStatus;
}

export interface ResolvedSessionPlan {
  curriculumId: string;
  slots: ResolvedSessionSlot[];
  /** Set when schedule length and curriculum length needed packing adjustments */
  mismatch?: string;
  teachingDayCount: number;
  levelCount: number;
}

function kindOf(level: LevelLite): 'required' | 'optional' | 'flex' {
  return level.kind || 'required';
}

/**
 * Pack curriculum levels into exactly `sessionCount` teaching days.
 * Keeps the lowest-numbered priorities when the curriculum is too long: 1 is
 * most important, then 2, then 3, and so on. Teaching order is preserved among
 * the lessons that remain. Missing and legacy zero priorities use curriculum
 * order, so older curricula continue pruning later lessons first.
 */
export function packForSessionCount(
  levels: LevelLite[],
  sessionCount: number
): { levelIds: string[]; mismatch?: string } {
  if (!levels.length) {
    return { levelIds: [], mismatch: 'No curriculum levels' };
  }

  if (sessionCount <= 0) {
    return {
      levelIds: levels.map((l) => l.id),
      mismatch: 'No teaching days on schedule; showing full curriculum',
    };
  }

  if (sessionCount === levels.length) {
    return { levelIds: levels.map((l) => l.id) };
  }

  if (sessionCount > levels.length) {
    const ids = levels.map((l) => l.id);
    const flexId =
      levels.find((l) => kindOf(l) === 'flex')?.id ||
      levels[levels.length - 1]?.id;
    while (flexId && ids.length < sessionCount) {
      ids.push(flexId);
    }
    return {
      levelIds: ids,
      mismatch: `Schedule has ${sessionCount} days; curriculum has ${levels.length} levels`,
    };
  }

  // sessionCount < levels.length — rank for retention, then restore teaching order.
  const working = levels
    .map((level, index) => ({
      level,
      index,
      priority: Number.isFinite(level.priority) && Number(level.priority) > 0
        ? Number(level.priority)
        : index + 1,
    }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .slice(0, sessionCount)
    .sort((a, b) => a.index - b.index)
    .map(({ level }) => level);

  const kept = new Set(working.map((l) => l.id));
  const droppedRequired = levels.filter(
    (l) => kindOf(l) === 'required' && !kept.has(l.id)
  );

  return {
    levelIds: working.map((l) => l.id),
    mismatch: droppedRequired.length
      ? `Had to omit ${droppedRequired.length} required ${droppedRequired.length === 1 ? 'lesson' : 'lessons'} to fit ${sessionCount} days`
      : undefined,
  };
}

function stripClassPrefix(title: string): string {
  return title.replace(/^Class\s+\d+\s*:\s*/i, '').trim() || title;
}

function todayYmd(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Resolve the effective session plan for a squad.
 * Uses persisted squad.sessionPlan when it matches curriculum + slot count;
 * otherwise packs from teaching dates × curriculum levels (Pass 1 client-side).
 */
export function resolveSessionPlan(
  squad: Pick<
    Squad,
    | 'curriculumId'
    | 'schedule'
    | 'timezone'
    | 'sessionPlan'
    | 'completedLevels'
    | 'currentLessonId'
    | 'classStatus'
    | 'totalClasses'
  >,
  levels: LevelLite[],
  curriculumId: string
): ResolvedSessionPlan {
  const teachingDates = listSquadTeachingDates(squad);
  const teachingDayCount =
    teachingDates.length ||
    (typeof squad.totalClasses === 'number' && squad.totalClasses > 0
      ? squad.totalClasses
      : 0);
  const levelCount = levels.length;

  const completedSlot = (levelId: string) => (squad.completedLevels || []).includes(levelId)
    || (squad.classStatus === 'current' && squad.currentLessonId === levelId);
  const persisted = squad.sessionPlan;
  const canUsePersisted =
    persisted &&
    persisted.curriculumId === curriculumId &&
    Array.isArray(persisted.slots) &&
    persisted.slots.length > 0 &&
    (persisted.slots.some(slot => slot.curriculumId) || teachingDayCount === 0 || persisted.slots.length === teachingDayCount);

  let levelIds: string[];
  let dates: (string | undefined)[];
  let eventIds: (string | undefined)[];
  let mismatch: string | undefined;

  if (canUsePersisted && persisted) {
    levelIds = persisted.slots.map((s) => s.levelId);
    dates = persisted.slots.map((s, i) => persisted.slots.some(slot => slot.curriculumId)
      ? (completedSlot(s.levelId) ? s.date || teachingDates[i] : teachingDates[i] || s.date)
      : s.date || teachingDates[i]);
    if (teachingDayCount && teachingDayCount !== persisted.slots.length) {
      mismatch = `The curriculum plan has ${persisted.slots.length} sessions; the schedule has ${teachingDayCount}. Update the curriculum plan in Squad details.`;
    }
    eventIds = persisted.slots.map((s) => s.eventId);
  } else {
    const packed = packForSessionCount(levels, teachingDayCount || levels.length);
    levelIds = packed.levelIds;
    mismatch = packed.mismatch;
    dates = levelIds.map((_, i) => teachingDates[i]);
    eventIds = levelIds.map(() => undefined);
  }

  const completed = new Set(squad.completedLevels || []);
  const currentLevelId = squad.currentLessonId || '1';
  const isLive = squad.classStatus === 'current';

  let nextSlotIndex = levelIds.findIndex((id) => !completed.has(id));
  if (nextSlotIndex < 0) nextSlotIndex = Math.max(0, levelIds.length - 1);

  let liveSlotIndex = isLive
    ? levelIds.findIndex((id) => id === currentLevelId)
    : -1;
  if (isLive && liveSlotIndex < 0) {
    liveSlotIndex = nextSlotIndex;
  }

  const slots: ResolvedSessionSlot[] = levelIds.map((levelId, i) => {
    const level = levels.find((l) => l.id === levelId);
    let status: SessionSlotStatus = 'future';
    if (liveSlotIndex === i) status = 'live';
    else if (completed.has(levelId)) status = 'completed';
    else if (i === nextSlotIndex) status = 'next';
    else status = 'future';

    return {
      index: i + 1,
      levelId,
      curriculumId: canUsePersisted ? persisted?.slots[i]?.curriculumId || curriculumId : curriculumId,
      sourceLevelId: canUsePersisted ? persisted?.slots[i]?.sourceLevelId || levelId : levelId,
      levelTitle: stripClassPrefix(level?.title || `Level ${levelId}`),
      date: dates[i],
      eventId: eventIds[i],
      status,
    };
  });

  return {
    curriculumId,
    slots,
    mismatch,
    teachingDayCount: teachingDayCount || levelIds.length,
    levelCount,
  };
}

/** Default pager index (0-based): live slot, else next, else 0. */
export function getDefaultPagerIndex(plan: ResolvedSessionPlan): number {
  const live = plan.slots.findIndex((s) => s.status === 'live');
  if (live >= 0) return live;
  const next = plan.slots.findIndex((s) => s.status === 'next');
  if (next >= 0) return next;
  return 0;
}

export function formatSlotDate(date?: string): string {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const dt = new Date(Date.UTC(y, m - 1, d));
  const today = todayYmd();
  const weekday = dt.toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  const monthDay = dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  if (date === today) return `Today · ${weekday}, ${monthDay}`;
  return `${weekday}, ${monthDay}`;
}

export function formatSquadSubtitle(squad: Pick<Squad, 'startGrade' | 'endGrade' | 'schedule' | 'term' | 'termId'>): string {
  const parts: string[] = [];
  const start = String(squad.startGrade || '').trim();
  const end = String(squad.endGrade || '').trim();
  if (start && end && start !== end) parts.push(`Grades ${start}–${end}`);
  else if (start || end) parts.push(`Grade ${start || end}`);

  const mainDate = String(squad.schedule?.mainDate || '').trim();
  const dow = String(squad.schedule?.dayOfWeek || '').trim();
  if (mainDate) {
    parts.push(formatSlotDate(mainDate));
  } else if (dow) {
    const names: Record<string, string> = {
      SUN: 'Sundays',
      MON: 'Mondays',
      TUE: 'Tuesdays',
      WED: 'Wednesdays',
      THU: 'Thursdays',
      FRI: 'Fridays',
      SAT: 'Saturdays',
    };
    parts.push(names[dow] || dow);
  }

  const term = String(squad.term || squad.termId || '').trim();
  if (term) {
    // winter2026 → Winter 2026
    const m = term.match(/^([a-zA-Z]+)(\d{4})$/);
    if (m) {
      parts.push(`${m[1].charAt(0).toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}`);
    } else {
      parts.push(term);
    }
  }

  return parts.join(' · ');
}
